import clsx from 'clsx';
import React, { useState } from 'react';

import { PrimaryButton } from '@components/Button/variants';
import { generateTrackingAttributes } from '@utils/tracking';

import { createLeadLockedHomepage } from '../../application/javascripts/forms/locked-homepage-lead-capture';
import GdprOptIn, { GdprColorScheme } from './GdprOptIn';

interface LeadDetails {
  article_title?: string;
  briefing_title?: string;
  newsletter_name?: string;
  org_chart_title?: string;
  homepage_conversion?: boolean;
  project_name?: string;
}

interface LockedLeadCaptureFormProps {
  buttonText: string;
  colorScheme?: GdprColorScheme;
  emailPlaceholderText?: string;
  endpointUri: string;
  forceEmailAuth?: boolean;
  formLocation?: string;
  isGdprLead?: boolean;
  leadDetails?: LeadDetails;
  leadType: string;
  redirectUri: string;
  shareGuid?: string;
  trackingProps?: { [key: string]: string };
}

export default function LockedLeadCaptureForm({
  buttonText,
  colorScheme = 'light',
  emailPlaceholderText = 'Enter your email',
  endpointUri,
  forceEmailAuth = false,
  formLocation,
  isGdprLead = false,
  leadDetails = {},
  leadType,
  redirectUri,
  shareGuid,
  trackingProps = {},
}: LockedLeadCaptureFormProps) {
  const trackClick = Object.keys(trackingProps).length > 0;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    setIsSubmitting(true);
    e.preventDefault();

    await createLeadLockedHomepage(e);

    setIsSubmitting(false);
  };

  return (
    <div data-testid="lock-lead-capture-form">
      <div className="get-started-form-group">
        <form className="get-started-action-group">
          {isGdprLead && <GdprOptIn colorScheme={colorScheme} />}
          <span className="mx-auto flex w-full flex-col items-end xs:flex-row sm:w-[384px]">
            <input
              type="text"
              className="get-started-input !mb-8 !mr-0 h-40 !border-2 !border-solid !border-primary-magenta !px-8 !py-10 !text-black xs:!mb-0"
              placeholder={emailPlaceholderText}
            />
            <PrimaryButton
              as="button"
              type="submit"
              className={clsx(
                'get-started-button !min-w-auto h-40 !w-auto !min-w-[141px] flex-shrink-0 !text-[16px] !leading-[1] hover:!bg-primary-pink hover:!text-primary-magenta',
                {
                  'track-click': trackClick,
                }
              )}
              data-submit-uri={endpointUri}
              data-redirect-uri={redirectUri}
              data-lead-type={leadType}
              data-force-email-auth={forceEmailAuth}
              data-lead-details={JSON.stringify(leadDetails)}
              data-form-location={formLocation}
              data-share-guid={shareGuid}
              disabled={isSubmitting}
              onClick={handleSubmit}
              size="small"
              {...generateTrackingAttributes({
                name: 'subscribe',
                page: window.location.pathname,
                ...trackingProps,
              })}
            >
              {buttonText}
            </PrimaryButton>
          </span>
          <div className="get-started-error mx-auto !mt-8 flex max-w-none !items-center !bg-transparent !pt-0 !text-left !font-suisse-screen !text-disclosure-sm !text-primary-magenta sm:max-w-[384px]"></div>
        </form>
      </div>
    </div>
  );
}
